// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import Zone from "@insite/client-framework/Components/Zone";
import { getProductsDataView } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import { HideFooter, HideHeader } from "@insite/client-framework/Types/FieldDefinition";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import Modals from "@insite/content-library/Components/Modals";
import Page from "@insite/mobius/Page";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import loadPreviouslyPurchased from "@pace/App/Handlers/xPreviouslyPurchased/LoadPreviouslyPurchased";
import { CustomAppState } from "@pace/App/Store/Reducers";
import {
    getPreviouslyPurchasedDataView,
    getPreviouslyPurchaseProductsInfo,
} from "@pace/App/Store/Selectors/PreviouslyPurchasedSelectors";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";

const mapStateToProps = (state: CustomAppState) => {
    const filters = state.xPreviouslyPurchasedFilterList.previouslyPurchasedFilters;
    const previouslyPurchasedDataView = getPreviouslyPurchasedDataView(state, filters);
    const productNumbers = previouslyPurchasedDataView.value?.map(({ productErpNumber }) => productErpNumber);
    const previouslyPurchasedProductsModel = getProductsDataView(state, { productNumbers });
    return {
        previouslyPurchasedParameters: filters,
        previouslyPurchasedDataView,
        previouslyPurchasedProducts: previouslyPurchasedProductsModel.value || [],
        previouslyPurchasedProductsInfo: getPreviouslyPurchaseProductsInfo(state, filters),
    };
};

const mapDispatchToProps = {
    loadPreviouslyPurchased,
};

export const PreviouslyPurchasedPageDataContext = React.createContext<{
    products: ProductModel[];
    productInfoList: SafeDictionary<ProductInfo>;
}>({
    products: [],
    productInfoList: {},
});

type Props = PageProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const PreviouslyPurchasedPage = ({
    id,
    previouslyPurchasedDataView,
    previouslyPurchasedParameters,
    previouslyPurchasedProducts,
    previouslyPurchasedProductsInfo,
    loadPreviouslyPurchased,
}: Props) => {
    useEffect(() => {
        // if this is undefined it means someone changed the filters and we haven't loaded the new collection yet
        if (!previouslyPurchasedDataView.value && !previouslyPurchasedDataView.isLoading) {
            loadPreviouslyPurchased(previouslyPurchasedParameters);
        }
    }, [previouslyPurchasedDataView.value, previouslyPurchasedDataView.isLoading, previouslyPurchasedParameters]);

    return (
        <Page>
            <PreviouslyPurchasedPageDataContext.Provider
                value={{ products: previouslyPurchasedProducts, productInfoList: previouslyPurchasedProductsInfo }}
            >
                <Zone contentId={id} zoneName="Content" />
            </PreviouslyPurchasedPageDataContext.Provider>
            <Modals />
        </Page>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(PreviouslyPurchasedPage),
    definition: {
        hasEditableUrlSegment: true,
        hasEditableTitle: true,
        supportsCategorySelection: true,
        pageType: "System",
        fieldDefinitions: [HideHeader, HideFooter],
    },
};
export default pageModule;

export const PreviouslyPurchasedPageContext = "PreviouslyPurchasedPage";
